import { useMsal } from "@azure/msal-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { AugmentedShipment } from "~/features/shipment-management/hooks/use-get-shipment.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { groupRemarksOrCheckCallsByDate } from "~/features/shipment-management/utils/group-remarks-by-date.ts";
import type { ShipmentRemark } from "~/shared/schemas/shipment-management-schema.ts";
import { newRemarkResponseSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export const postNewRemark = async (messageData: {
	message: string;
	poNumber: string;
}) => {
	const url = apiRoutes.POSTNEWREMARK.replace(
		":poNumber",
		messageData.poNumber,
	);
	const resp = await postAndValidate(
		url,
		messageData.message,
		newRemarkResponseSchema,
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostNewRemark = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { accounts } = useMsal();
	const currentUserName = accounts[0]?.name ?? null;

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (messageData: { message: string; poNumber: string }) =>
			postNewRemark(messageData),
		onSuccess: (savedRemark) => {
			const newRemark: ShipmentRemark = {
				id: savedRemark.remarkId,
				dateEntered: new Date().toISOString(),
				text: savedRemark.remarkText,
				contactInfo: {
					name: currentUserName,
					phone: null,
					extension: null,
					email: null,
				},
			};

			queryClient.setQueriesData<AugmentedShipment>(
				{ queryKey: ["shipment"] },
				(old) => {
					if (!old) {
						return old;
					}
					const remarks = [...(old.remarks ?? []), newRemark];
					return {
						...old,
						remarks,
						groupedRemarks: groupRemarksOrCheckCallsByDate(remarks),
					};
				},
			);
			showSuccess("Remark added successfully.");
		},
		onError: () => {
			showError("Failed to add remark. Please try again.");
		},
	});

	return {
		postNewRemark: mutate,
		postNewRemarkAsync: mutateAsync,
		isPostingNewRemark: isPending,
		error,
		data,
		resetError: reset,
	};
};
